import {
  FunctionComponent,
  PropsWithChildren,
  useSyncExternalStore,
} from 'react'

import { IntercomProvider } from 'react-use-intercom'
import { createGlobalStyle } from 'styled-components'

import { APP } from 'src/Configs/App'
import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'
import browserHistory from 'src/Router/browser-history'

const SCHEDULE_CHECKOUT_PATHS = [
  '/campaigns/schedule/',
  '/campaigns/scheduleTikTok/',
  '/campaigns/scheduleAds/',
]

const FULL_SCREEN_ROUTE_MODAL_PATHS = [
  '/sounds/new/',
  '/sounds/inProgress/',
  '/sounds/submitted/',
]

const shouldHideIntercomOnMobile = (): boolean => {
  const { pathname } = window.location
  return SCHEDULE_CHECKOUT_PATHS.some((path) => pathname.startsWith(path))
}

const shouldHideIntercomGlobally = (): boolean => {
  const { pathname } = window.location
  return FULL_SCREEN_ROUTE_MODAL_PATHS.some((path) => pathname.startsWith(path))
}

const subscribeToHistory = (callback: () => void): (() => void) =>
  browserHistory.listen(callback)

const useHideIntercomOnMobile = (): boolean =>
  useSyncExternalStore(subscribeToHistory, shouldHideIntercomOnMobile)

const useHideIntercomGlobally = (): boolean =>
  useSyncExternalStore(subscribeToHistory, shouldHideIntercomGlobally)

const GlobalStyles = createGlobalStyle<{
  $hideOnMobile: boolean
  $hideGlobally: boolean
}>`
  .intercom-lightweight-app-launcher {
    bottom: 16px !important;
    right: 16px !important;
    @media (min-width: ${MAX_MOBILE_WIDTH}) {
      display: block;
      bottom: 20px !important;
    }
  }

  .intercom-app div:has(> .intercom-launcher-frame) {
    bottom: 16px !important;
    right: 63px !important;
    @media (min-width: ${MAX_MOBILE_WIDTH}) {
      bottom: 20px !important;
      right: 20px !important;
    }
  }

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    .intercom-launcher-frame {
      z-index: 1 !important;
    }
    .intercom-app div:nth-child(2) {
      width: 1px !important;
    }

    ${({ $hideOnMobile }) =>
      $hideOnMobile &&
      `
      .intercom-lightweight-app-launcher,
      .intercom-launcher-frame,
      .intercom-app div:has(> .intercom-launcher-frame) {
        display: none !important;
      }
    `}
  }

  body:has(.ant-modal-root .ant-modal-wrap:not([style*="display: none"])) {
    .intercom-lightweight-app-launcher,
    .intercom-launcher-frame,
    .intercom-app div:has(> .intercom-launcher-frame) {
      display: none !important;
    }
  }

  ${({ $hideGlobally }) =>
    $hideGlobally &&
    `
    .intercom-lightweight-app-launcher,
    .intercom-launcher-frame,
    .intercom-app div:has(> .intercom-launcher-frame) {
      display: none !important;
    }
  `}
`

export const IntercomWrapper: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const hideOnMobile = useHideIntercomOnMobile()
  const hideGlobally = useHideIntercomGlobally()

  return (
    <IntercomProvider appId={APP.INTERCOM_APP_ID}>
      <GlobalStyles $hideOnMobile={hideOnMobile} $hideGlobally={hideGlobally} />
      <div>{children}</div>
    </IntercomProvider>
  )
}
